import { z } from 'zod';
import { DATABASES } from '@fortem-monorepo/constants';
const CURRENCY_INSTRUMENT_TYPE_ID = 3;
const BOND_INSTRUMENT_TYPE_ID = 2;
// -----------------------------------------------------------------------------
// Shared primitives
// -----------------------------------------------------------------------------
const id = z.number().int();
const database = z.enum(DATABASES);
const priceFormat = z.union([z.literal('decimal'), z.literal('percent')]);
const hasValue = (value) => {
    if (typeof value === 'string')
        return value.trim().length > 0;
    return value !== null && value !== undefined;
};
// -----------------------------------------------------------------------------
// Base trade shape
// Keep this as a plain shape. No refine/superRefine here.
// -----------------------------------------------------------------------------
const baseTradeShape = {
    strategy_id: id,
    instrument_type_id: id,
    instrument_id: id,
    currency_id: id,
    price: z.number(),
    quantity: z.number(),
    unit_multiplier: z.number(),
    notional: z.number(),
    counterparty_id: id,
    instruction_date: z.iso.date().nullish(),
    trade_datetime_utc: z.iso.datetime(),
    settlement_date: z.iso.date(),
    trader_id: id.nullish(),
    trade_type: z.string(),
    // Currency trade fields
    reference_instrument_type_id: id.nullish(),
    reference_instrument_id: id.nullish(),
    // Metadata
    price_format: priceFormat,
    tags: z.array(z.string()).nullish(),
};
// -----------------------------------------------------------------------------
// Strategy-specific shapes
// -----------------------------------------------------------------------------
const fundAmcShape = {
    accrued_interests: z.number().nullish(),
    agent: z.string().nullish(),
    agent_ref: z.string().nullish(),
    // OLDDB
    structure_name: z.string().nullish(),
    top_level_bucket_id: id.nullish(),
    investment_bucket_id: id.nullish(),
    wrapper_bucket_id: id.nullish(),
    /**
     * Prefer this over z.union([z.number(), z.nan()])
     * Convert empty/NaN values to null in the form input layer.
     */
    locked_ytm: z.number().nullish(),
    database,
};
const bespokeShape = {
    client_id: id,
    comment: z.string().nullish(),
};
// -----------------------------------------------------------------------------
// Dependency schemas
//
// These are intentionally small.
// Each custom rule declares exactly which fields it needs.
// This is what makes `when` readable.
// -----------------------------------------------------------------------------
const traderRequiredDeps = z.object({
    instrument_type_id: id.nullish(),
    trader_id: id.nullish(),
});
const accruedInterestsRequiredDeps = z.object({
    strategy_type: z.enum(['FUND', 'AMC', 'BESPOKE']),
    instrument_type_id: id.nullish(),
    accrued_interests: z.number().nullish(),
});
const referenceInstrumentTypeRequiredDeps = z.object({
    instrument_type_id: id.nullish(),
    reference_instrument_type_id: id.nullish(),
});
const referenceInstrumentRequiredDeps = z.object({
    instrument_type_id: id.nullish(),
    reference_instrument_id: id.nullish(),
});
const dateCheckDeps = z.object({
    instruction_date: z.string().nullish(),
    trade_datetime_utc: z.string().nullish(),
    settlement_date: z.string().nullish(),
});
// const structureNameRequiredDeps = z.object({
//   strategy_type: z.enum(STRATEGY_TYPES).nullish(),
//   instrument_type_id: id.nullish(),
//   structure_name: z.string().nullish(),
// })
// -----------------------------------------------------------------------------
// Conditional business rules
//
// This is Option B, but kept readable.
// Each refine is one business rule.
// -----------------------------------------------------------------------------
const withTradeConditionalRules = (schema) => schema
    .refine((data) => data.strategy_type === 'BESPOKE' ||
    data.instrument_type_id !== BOND_INSTRUMENT_TYPE_ID ||
    data.accrued_interests != null, {
    path: ['accrued_interests'],
    error: 'Accrued interests are required for FUND/AMC bond trades; zero is allowed.',
    when(payload) {
        return accruedInterestsRequiredDeps.safeParse(payload.value).success;
    },
})
    /**
     * Rule:
     * If instrument_type_id is not currency, trader_id is required.
     */
    .refine((data) => {
    const result = traderRequiredDeps.safeParse(data);
    if (!result.success)
        return true;
    const { instrument_type_id, trader_id } = result.data;
    if (instrument_type_id == null)
        return true;
    if (instrument_type_id === CURRENCY_INSTRUMENT_TYPE_ID)
        return true;
    return hasValue(trader_id);
}, {
    path: ['trader_id'],
    error: 'Trader is required for non-currency trades.',
    when(payload) {
        return traderRequiredDeps.safeParse(payload.value).success;
    },
})
    /**
     * Rule:
     * If instrument_type_id is currency, reference_instrument_type_id is required.
     */
    .refine((data) => {
    const result = referenceInstrumentTypeRequiredDeps.safeParse(data);
    if (!result.success)
        return true;
    const { instrument_type_id, reference_instrument_type_id } = result.data;
    if (instrument_type_id !== CURRENCY_INSTRUMENT_TYPE_ID)
        return true;
    return hasValue(reference_instrument_type_id);
}, {
    path: ['reference_instrument_type_id'],
    error: 'Reference instrument type is required for currency trades.',
    when(payload) {
        return referenceInstrumentTypeRequiredDeps.safeParse(payload.value).success;
    },
})
    /**
     * Rule:
     * If instrument_type_id is currency, reference_instrument_id is required.
     */
    .refine((data) => {
    const result = referenceInstrumentRequiredDeps.safeParse(data);
    if (!result.success)
        return true;
    const { instrument_type_id, reference_instrument_id } = result.data;
    if (instrument_type_id !== CURRENCY_INSTRUMENT_TYPE_ID)
        return true;
    return hasValue(reference_instrument_id);
}, {
    path: ['reference_instrument_id'],
    error: 'Reference instrument is required for currency trades.',
    when(payload) {
        return referenceInstrumentRequiredDeps.safeParse(payload.value).success;
    },
})
    /**
     * Rule:
     * instruction_date must be <= trade_datetime_utc
     */
    .refine((data) => {
    const result = dateCheckDeps.safeParse(data);
    if (!result.success)
        return true;
    const { instruction_date, trade_datetime_utc } = result.data;
    if (!instruction_date || !trade_datetime_utc)
        return true;
    const instructionDate = new Date(instruction_date);
    const tradeDate = new Date(trade_datetime_utc);
    return instructionDate <= tradeDate;
}, {
    path: ['instruction_date'],
    error: 'Instruction date must be less than or equal to trade date.',
    when(payload) {
        return dateCheckDeps.safeParse(payload.value).success;
    },
})
    /**
     * Rule:
     * trade_datetime_utsc must be <= settlement_date
     */
    .refine((data) => {
    const result = dateCheckDeps.safeParse(data);
    if (!result.success)
        return true;
    const { trade_datetime_utc, settlement_date } = result.data;
    if (!trade_datetime_utc || !settlement_date)
        return true;
    // Must make trade_date the date part, not using time
    const tradeDate = new Date(trade_datetime_utc);
    tradeDate.setHours(0, 0, 0, 0);
    const settlementDate = new Date(settlement_date);
    return tradeDate <= settlementDate;
}, {
    path: ['settlement_date'],
    error: 'Settlement date must be greater than or equal to the trade date.',
    when(payload) {
        return dateCheckDeps.safeParse(payload.value).success;
    },
});
// /**
//  * Rule:
//  * If strategy is FUND or AMC, and instrument is currency,
//  * structure_name is required.
//  */
// .refine(
//   (data) => {
//     const result = structureNameRequiredDeps.safeParse(data)
//     if (!result.success) return true
//     const { strategy_type, instrument_type_id, structure_name } = result.data
//     const isFundOrAmc = strategy_type === 'FUND' || strategy_type === 'AMC'
//     const isCurrency = instrument_type_id === CURRENCY_INSTRUMENT_TYPE_ID
//     if (!isFundOrAmc) return true
//     if (!isCurrency) return true
//     return hasValue(structure_name)
//   },
//   {
//     path: ['structure_name'],
//     error: 'Structure name is required for currency Fund/AMC trades.',
//     when(payload) {
//       return structureNameRequiredDeps.safeParse(payload.value).success
//     },
//   },
// )
// -----------------------------------------------------------------------------
// Final strategy schemas
// -----------------------------------------------------------------------------
export const fundStrategySchema = withTradeConditionalRules(z.object({
    ...baseTradeShape,
    ...fundAmcShape,
    strategy_type: z.literal('FUND'),
}));
export const amcStrategySchema = withTradeConditionalRules(z.object({
    ...baseTradeShape,
    ...fundAmcShape,
    strategy_type: z.literal('AMC'),
}));
export const bespokeStrategySchema = withTradeConditionalRules(z.object({
    ...baseTradeShape,
    ...bespokeShape,
    strategy_type: z.literal('BESPOKE'),
}));
// -----------------------------------------------------------------------------
// Final schema
// -----------------------------------------------------------------------------
export const bookTradeSchema = z.discriminatedUnion('strategy_type', [
    fundStrategySchema,
    amcStrategySchema,
    bespokeStrategySchema,
]);
export const updateTradeSchema = z
    .object({
    rowData: z.unknown().optional(),
    updatedField: z.string().optional(),
    oldValue: z.unknown().optional(),
    newValue: z.unknown().optional(),
})
    .superRefine((data, ctx) => {
    if (data.updatedField === 'accrued_interests' && !z.number().safeParse(data.newValue).success) {
        ctx.addIssue({
            code: 'custom',
            path: ['newValue'],
            message: 'Accrued interests must be a finite number; zero is allowed.',
        });
    }
});
export const updateBespokeTradeSchema = updateTradeSchema.refine((data) => data.updatedField !== 'accrued_interests', {
    path: ['updatedField'],
    error: 'Accrued interests are not supported for bespoke trades.',
});
export const deleteOldDBTradeSchema = z.object({
    blotterid: z.string(),
    productname: z.string(),
});
export const sendBespokeInvoiceSchema = z.object({
    value_date: z.string(),
});
